import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { MdBlock } from 'react-icons/md';
import { IoFlag } from 'react-icons/io5';
import { FancyCheckbox } from '../../../core/components/FancyCheckbox';
import { EmployeeInitialsDropdown } from '../../../core/components/EmployeeInitials';
import { TaskPriorityTag } from '../../../core/components/TaskPriorityTag';
import { apiError } from '../../../core/toastMessages';
import SnoozeOccurrenceModal from './SnoozeOccurrenceModal';
import {
  TaskOccurrence,
  useSetOccurrenceStatusByIdMutation,
  useRevertOccurrenceCompletionMutation,
  useRevertOccurrenceSnoozeMutation,
} from '../../../core/api/rtk/taskTemplatesApi';
import { selectCurrentEmployee } from '../../tasks/reducer';
import { setCurrentEmployee as setGlobalEmployee } from '../../tasks/actions';

interface TaskOccurrenceCardProps {
  occurrence: TaskOccurrence;
  onOpen?: (occurrence: TaskOccurrence) => void;
  className?: string;
  /** Mirrors TaskListItem — daily view leaves this false. */
  showPriorityTag?: boolean;
  displayBlockedButton?: boolean;
  /** Extra rows rendered inside `.task-details` below the name. Maintenance
   * uses this for due-date + location, mirroring TaskListItem's slot. */
  additionalLines?: React.ReactNode;
}

// completionDelay must stay in sync with `.task.transitioning` timing in _new-tasks.scss.
const COMPLETION_DELAY_MS = 500;

export const TaskOccurrenceCard = ({
  occurrence,
  onOpen,
  className,
  showPriorityTag = false,
  displayBlockedButton = true,
  additionalLines,
}: TaskOccurrenceCardProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const globalEmployee = useSelector(selectCurrentEmployee);
  const [setOccurrenceStatus] = useSetOccurrenceStatusByIdMutation();
  const [revertOccurrence] = useRevertOccurrenceCompletionMutation();
  const [revertSnooze] = useRevertOccurrenceSnoozeMutation();
  const [isTogglingSnooze, setIsTogglingSnooze] = useState(false);
  const [snoozeModalOpen, setSnoozeModalOpen] = useState(false);

  const initiallyCompleted = occurrence.status === 'Completed';
  const initiallySnoozed = occurrence.status === 'Snoozed';
  const [completed, setCompleted] = useState(initiallyCompleted);
  const [snoozed, setSnoozed] = useState(initiallySnoozed);
  const [transitioning, setTransitioning] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState<number>(
    occurrence.employeeId ?? (initiallyCompleted ? 0 : globalEmployee ?? 0)
  );

  useEffect(() => {
    const isComplete = occurrence.status === 'Completed';
    setCompleted(isComplete);
    setSnoozed(occurrence.status === 'Snoozed');
    setCurrentEmployee(occurrence.employeeId ?? (isComplete ? 0 : globalEmployee ?? 0));
    setTransitioning(false);
  }, [occurrence.id, occurrence.status, occurrence.employeeId, globalEmployee]);

  useEffect(() => {
    if (!transitioning) return;
    const timeout = setTimeout(async () => {
      try {
        if (completed) {
          await setOccurrenceStatus({
            occurrenceId: occurrence.id,
            status: 'Completed',
            employeeId: currentEmployee || undefined,
          }).unwrap();
        } else {
          await revertOccurrence({ occurrenceId: occurrence.id }).unwrap();
        }
      } catch {
        setCompleted(occurrence.status === 'Completed');
        setTransitioning(false);
      }
    }, COMPLETION_DELAY_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed]);

  const completeTask = () => {
    if (transitioning) return;
    setTransitioning(true);
    setCompleted((prev) => !prev);
  };

  // Local-only. Persisting on every dropdown fire would trigger one PATCH per card;
  // the value is folded into the status update when the row is checked off instead.
  const handleEmployeeSelect = (next: number) => {
    setCurrentEmployee(next);
    dispatch(setGlobalEmployee(next));
  };

  // Snoozing needs the notes dialog; unsnooze is immediate — different code paths.
  const handleSnoozeButton = (): void => {
    if (isTogglingSnooze || completed) return;
    if (snoozed) {
      void unsnoozeNow();
      return;
    }
    setSnoozeModalOpen(true);
  };

  const unsnoozeNow = async (): Promise<void> => {
    setIsTogglingSnooze(true);
    try {
      await revertSnooze({ occurrenceId: occurrence.id }).unwrap();
      setSnoozed(false);
    } catch (err) {
      apiError(err);
    } finally {
      setIsTogglingSnooze(false);
    }
  };

  const handleSnoozeSubmit = async (reason: string): Promise<void> => {
    setIsTogglingSnooze(true);
    try {
      await setOccurrenceStatus({
        occurrenceId: occurrence.id,
        status: 'Snoozed',
        notes: reason,
        employeeId: currentEmployee || undefined,
      }).unwrap();
      setSnoozed(true);
      setSnoozeModalOpen(false);
    } catch (err) {
      apiError(err);
    } finally {
      setIsTogglingSnooze(false);
    }
  };

  const subtitle = occurrence.task?.subTitle;

  return (
    <div
      className={`task${className ? ' ' + className : ''}${completed ? ' completed' : ''}${
        transitioning ? ' transitioning' : ''
      }`}
    >
      <div>
        {!snoozed ? (
          <FancyCheckbox
            isChecked={completed}
            onClick={completeTask}
            size={30}
            title={
              completed
                ? intl.formatMessage({ id: 'uncompleteTaskTitleText' })
                : intl.formatMessage({ id: 'completeTaskTitleText' })
            }
          />
        ) : (
          <div className="task-snoozed no-select" role="presentation">
            <MdBlock />
          </div>
        )}
        <EmployeeInitialsDropdown
          selectable={!completed}
          selectedEmployee={currentEmployee}
          onEmployeeSelect={handleEmployeeSelect}
          listHeight={150}
        />
        <button
          className="task-details"
          title={intl.formatMessage({ id: 'viewTaskDetails' })}
          onClick={() => onOpen && onOpen(occurrence)}
          type="button"
        >
          <span className="task-name">{occurrence.task?.name || occurrence.name || '—'}</span>
          {subtitle ? <span className="task-description">{subtitle}</span> : null}
          {additionalLines ? <div className="task-extra-lines">{additionalLines}</div> : null}
        </button>
      </div>
      <div className="details">
        {!completed && displayBlockedButton && (
          <button
            className={`button snooze-button${snoozed ? ' snoozed' : ''}`}
            title={
              snoozed
                ? intl.formatMessage({ id: 'unblock', defaultMessage: 'Unblock' })
                : intl.formatMessage({ id: 'block', defaultMessage: 'Block' })
            }
            onClick={handleSnoozeButton}
            disabled={isTogglingSnooze || completed}
            type="button"
          >
            <IoFlag />
          </button>
        )}
        {showPriorityTag && (
          <TaskPriorityTag
            task={{
              priority: occurrence.priority ?? occurrence.task?.priority ?? null,
            }}
          />
        )}
      </div>
      <SnoozeOccurrenceModal
        isOpen={snoozeModalOpen}
        occurrence={occurrence}
        onClose={() => setSnoozeModalOpen(false)}
        onSubmit={handleSnoozeSubmit}
        isSubmitting={isTogglingSnooze}
      />
    </div>
  );
};

export default TaskOccurrenceCard;
